import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@progress/kendo-react-all';
import { SvgIcon } from '@progress/kendo-react-common';
import { caretAltDownIcon, trashIcon, searchIcon } from "@progress/kendo-svg-icons";
import './FilterDropDown.scss';
import useProfileData from '../services/ProfileDataService';
import { getCurrentLine, subscribeToCurrentLineChanges } from '../services/CurrentLineService';
import { InputPrefix, TextBox, InputSuffix } from "@progress/kendo-react-inputs";
import FilterSection from './FilterSection';
import { useGetSelectionsByCodeNum } from '../Helpers/Hooks/UseGetSelectionsByCodeNum';
import { SidePanelGeneric } from './SidePanelGeneric';
import { hardCodesList } from '../Utilities/hardCodesFilter';
import { DPSA_LINE } from '../Utilities/constants';

const FilterDropdown = ({ onApplyFilter, item, numFiltersApplied, selectedOptions, setSelectedOptions, handleShowMessage }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const modalRef = useRef(null);
  const { token } = useProfileData(false);  
  const [currentLine, setCurrentLine] = useState(getCurrentLine().ProdFamilyId);
  const [optionsData, setOptionsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');  
  const [filterTitle, setFilterTitle] = useState('');
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [dataModaldescription, setDataModalDescription] = useState();

  useEffect(() => {
    const unsubscribe = subscribeToCurrentLineChanges(newCurrentLine => {
      setCurrentLine(newCurrentLine.ProdFamilyId);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const { data: codeOptionsData } = useGetSelectionsByCodeNum({
    currentLine,
    token
  });  
  
  useEffect(() => {
    if (codeOptionsData) {
      setOptionsData(codeOptionsData);
      setFilteredData(codeOptionsData);
    }
  }, [codeOptionsData]);

  const numFilters = Object.keys(numFiltersApplied).length;

  // Toggle visibility of the modal
  const toggleModal = () => {
    setFilterTitle('');
    setIsInputVisible(false);
    setModalVisible(!modalVisible);
    setDataModalDescription(null);
  };

  const handleCheckboxChange = (event, filter) => {
    const option = event.target.value;
    const codeSelected = filter.codes.find(c => c.value === option);
    const newOption = {
      Value: option,
      codeStart: codeSelected.position,
      codeLength: codeSelected.length,
      codeItem: codeSelected.codeItem,
      description: codeSelected.description,
      ciDescription: filter.description
    };

    setSelectedOptions(prev => {
      const exists = prev.some(item => item.Value === option && item.codeItem === newOption.codeItem);
      return exists
        ? prev.filter(item => !(item.Value === newOption.Value && item.codeItem === newOption.codeItem))
        : [...prev, newOption];
    });
  };

  const applyFilter = () => {
    onApplyFilter(selectedOptions);
    setModalVisible(false);
    setDataModalDescription(null);
  };

  const filterData = (event) => {
    //Reset filter when there are no characters
    if(event === "") setFilteredData(optionsData);

    const searchValue = event.target.value.toLowerCase();

    const newFilteredData = optionsData.map((item) => {
      const matchesCodeDescription = item.description.toLowerCase().includes(searchValue.toLowerCase());
                
      const matchingCodes = item.codes.filter((code) =>
        code.codeItem.toString().includes(searchValue) ||
        code.value.toLowerCase().includes(searchValue.toLowerCase()) ||
        code.description.toLowerCase().includes(searchValue.toLowerCase())
      );  
      if (matchesCodeDescription || matchingCodes.length > 0) {
        return {
          ...item,
          codes: matchingCodes.length > 0 ? matchingCodes : item.codes
        };
      }
      return null;
    }).filter((item) => item !== null);

    setFilteredData(newFilteredData);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    filterData(event);
  };

  const clearSearch = () => {
    setSearchText('');
    filterData({ target: { value: '' } });
  };

  const handleSaveFilters = () => {
    // group selected options by 'description'
    const groupedFilters = selectedOptions.reduce((acc, option) => {
      const { description, Value, codeLength, codeStart, codeItem, ciDescription } = option;

      if (!acc.filters[description]) {
        acc.filters[description] = [];
        acc.codeDetails[description] = [];
      }

      if (!acc.filters[description].includes(Value)) {
        acc.filters[description].push(Value);
      }

      if (!acc.codeDetails[description].some(detail =>
        detail.codeLength === codeLength &&
        detail.codeStart === codeStart &&
        detail.codeItem === codeItem)) {
        acc.codeDetails[description].push({
          codeLength,
          codeStart,
          codeItem,
          ciDescription
        });
      }

      return acc;
    }, { filters: {}, codeDetails: {} });

    const newFilter = {
      title: filterTitle,
      lineNumber: currentLine,
      filters: groupedFilters.filters,
      codeDetails: groupedFilters.codeDetails
    };

    let savedFilters = JSON.parse(localStorage.getItem('savedFilters')) || [];

    if (savedFilters.length >= 10) {
      savedFilters.shift();
    }

    savedFilters.push(newFilter);
    localStorage.setItem('savedFilters', JSON.stringify(savedFilters));

    applyFilter();
    handleShowMessage("Your code string filters have been saved", "success");
  };

  return (
    <div className="filter-dropdown roboto-15-400 fs-17">
      <div>
        {getCurrentLine().PlantProdFamilyId && (
          <Button
            onClick={toggleModal}
            className="filter-button roboto-15-500 fs-15">
            <span className="return-text">Code String Filters</span>
            {numFilters > 0 ? ` (${numFilters})` : ' (0)'}
            <SvgIcon icon={caretAltDownIcon} size={'large'} color="black" />
          </Button>
        )}
      </div>
      {modalVisible && (
        <div className="modal-container">
          <div ref={modalRef} className="modal-content">
            <div className="modal-body container">
              <div className="filter-container">
                <div className='col-10 search-container'>
                  <TextBox
                    className='roboto-15-400'
                    value={searchText}
                    onChange={handleInputChange}
                    placeholder="Search code item number or name"
                    prefix={() => (
                      <InputPrefix>
                        {/*icon if needed */}
                      </InputPrefix>
                    )}
                    suffix={() => (
                      searchText ? (
                        <InputSuffix>
                          <button
                            className="clear-button close-icon"
                            onClick={clearSearch}
                            aria-label="Clear search"
                          >
                            <span className="material-symbols-outlined mr-3">
                              close
                            </span>
                          </button>
                        </InputSuffix>
                      ) : 
                      <span className="material-symbols-outlined mr-3">
                        search
                      </span>
                    )}
                  />
                </div>
              </div>
                    
              {filteredData?.length === 0 ? (
                <div className="roboto-error fs-17 text-center mt-3">
                  Format number not found for this product
                </div>
              ) : (
                filteredData?.map((filter, index) => (
                  <FilterSection
                    key={index}
                    title={ filter.codes[0].codeItem + ". " + filter.description}
                    options={ filter.codes.map((code) => code) }
                    selectedOptions={selectedOptions}
                    onCheckboxChange={(event) => handleCheckboxChange(event, filter)}
                    filter={filter}
                    setSelectedOptions={setSelectedOptions}
                    onSetDataModalDescription={(data) => setDataModalDescription(data)}
                  />
                ))
              )}
            </div>

            {!isInputVisible ? (
              <div className="modal-footer">
                <div>
                  <Button onClick={() => setIsInputVisible(true)} className="apply-button blue-text btn-save">
                    <span className="material-symbols-outlined">
                      save
                    </span>
                    Save filters
                  </Button>
                </div>
                <div className='footer-actions'>
                  <Button onClick={applyFilter}
                    className="apply-button return-button blue-text">
                    <span className="material-symbols-outlined">
                      check_circle
                    </span>
                    Apply Filters
                  </Button>
                  <Button onClick={toggleModal} className="primary-button return-button blue-text w-25">
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="modal-footer" >
                <div className="d-flex align-items-center gap-10 w-100">
                  <TextBox
                    className='roboto-15-400'
                    value={filterTitle}
                    onChange={(e) => setFilterTitle(e.value)}
                    placeholder="Name your filter configuration"
                    suffix={() => (
                      filterTitle && (
                        <InputSuffix>
                          <button
                            className="clear-button close-icon"
                            onClick={() => setFilterTitle('')}
                            aria-label="Clear filter title"
                          >
                            <span className="material-symbols-outlined mr-5">close</span>
                          </button>
                        </InputSuffix>
                      )
                    )}
                  />
                  <Button onClick={handleSaveFilters} className="apply-button return-button blue-text">
                    Save filters
                    <span className="material-symbols-outlined">
                      save
                    </span>
                  </Button>

                  <Button onClick={toggleModal} className="primary-button close-button blue-text">
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {dataModaldescription && (
        <SidePanelGeneric
          onClose={() => setDataModalDescription(null)}
          visible={Object.keys(dataModaldescription).length > 0}
          title={dataModaldescription.title}
        >
          <div className="list-container">
            <p>Variations:</p>
            <p>UNIT:</p>
            <ul className="list">
            {/*TODO: For Now we use hard code values*/}
            {dataModaldescription.title.split(".")[0] >= 111 && 
              dataModaldescription.title.split(".")[0] <= 135 &&
              getCurrentLine().ProdFamilyName === DPSA_LINE
                ? hardCodesList().map((value) => (
                    <li>
                      <span className="title">{value[0]}: </span>
                      <span className="description">{value[1]}</span>
                    </li>
                  ))
                : dataModaldescription.options.map(
                    ({ value, description }) => (
                      <li>
                        <span className="title">{value}: </span>
                        <span className="description">{description}</span>
                      </li>
                    )
                  )}
            </ul>
          </div>
        </SidePanelGeneric>
      )}
    </div>
  );
};

export default FilterDropdown;
